import React, { useState, useEffect, useCallback } from 'react';
import {
  Zap, Pencil, Check, X, RefreshCw, Wifi, WifiOff,
  Power, PowerOff, Plus, Trash2, Layers, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import {
  getRelays, controlRelay, renameRelay, getDevices, controlAllRelays,
  getRelayGroups, createRelayGroup, deleteRelayGroup, controlRelayGroup, getEspDevices
} from '@/lib/api';
import usePolling from '@/hooks/usePolling';
import { useAuth } from '@/context/AuthContext';

// ─── Relay Card ───────────────────────────────────────────────────────────────

const RelayCard = ({ relay, deviceName, espDeviceId = 'global', onToggle, onRename }) => {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(relay.name || `Röle ${relay.relay_number}`);
  const [toggling, setToggling] = useState(false);

  const handleToggle = async (val) => {
    setToggling(true);
    await onToggle(relay.relay_number, val, espDeviceId);
    setToggling(false);
  };

  const saveName = async () => {
    await onRename(relay.relay_number, editName, espDeviceId);
    setEditing(false);
  };

  return (
    <div
      className={`group relative rounded-xl border p-4 flex flex-col gap-3 transition-all duration-200 cursor-default
        ${relay.state
          ? 'bg-green-500/5 border-green-500/40 shadow-sm shadow-green-500/10'
          : 'bg-card border-border hover:border-muted-foreground/30'
        }`}
      data-testid={`relay-card-${relay.relay_number}`}
    >
      {/* Top row: number + status dot */}
      <div className="flex items-center justify-between">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold font-mono-tech transition-colors
          ${relay.state ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'}`}>
          {relay.relay_number}
        </div>
        <div className={`w-2 h-2 rounded-full transition-colors ${relay.state ? 'bg-green-500' : 'bg-muted-foreground/25'}`} />
      </div>

      {/* Name (editable) */}
      {editing ? (
        <div className="flex items-center gap-1">
          <Input value={editName} onChange={e => setEditName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && saveName()}
            className="h-7 text-xs px-2 flex-1" autoFocus
            data-testid={`relay-name-input-${relay.relay_number}`} />
          <button onClick={saveName} className="text-green-500 hover:text-green-600 p-1">
            <Check size={13} />
          </button>
          <button onClick={() => setEditing(false)} className="text-muted-foreground hover:text-destructive p-1">
            <X size={13} />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1 group/name">
          <p className="text-sm font-medium flex-1 leading-tight">{relay.name || `Röle ${relay.relay_number}`}</p>
          <button
            onClick={() => { setEditName(relay.name || `Röle ${relay.relay_number}`); setEditing(true); }}
            className="opacity-0 group-hover/name:opacity-100 text-muted-foreground hover:text-primary transition-opacity p-0.5"
            data-testid={`relay-edit-name-${relay.relay_number}`}
          >
            <Pencil size={11} />
          </button>
        </div>
      )}

      {/* Linked device */}
      {deviceName ? (
        <Badge variant="outline" className="text-[10px] w-fit gap-0.5 px-1.5 h-4">
          <Zap size={8} />{deviceName}
        </Badge>
      ) : <div className="h-4" />}

      {/* Toggle */}
      <div className="flex items-center justify-between mt-auto pt-1 border-t border-border/50">
        <span className={`text-xs font-medium ${relay.state ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
          {relay.state ? 'Açık' : 'Kapalı'}
        </span>
        <Switch
          checked={relay.state}
          onCheckedChange={handleToggle}
          disabled={toggling}
          className="data-[state=checked]:bg-green-500 scale-90"
          data-testid={`relay-toggle-${relay.relay_number}`}
        />
      </div>
    </div>
  );
};

// ─── Device Section ───────────────────────────────────────────────────────────

const DeviceSection = ({ esp, espDeviceId, relays, deviceMap, onToggle, onRename }) => {
  const [batchToggling, setBatchToggling] = useState(null);
  const activeCount = relays.filter(r => r.state).length;

  const handleBatch = async (state) => {
    setBatchToggling(state ? 'on' : 'off');
    for (const r of relays) await onToggle(r.relay_number, state, espDeviceId);
    setBatchToggling(null);
  };

  return (
    <div>
      {/* Section header */}
      <div className="flex items-center gap-3 mb-4">
        {esp ? (
          <>
            <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${esp.online ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
            <span className="font-semibold text-base" style={{fontFamily:'Outfit,sans-serif'}}>{esp.name}</span>
            {esp.zone && <Badge variant="outline" className="text-xs">{esp.zone}</Badge>}
            <Badge className={`text-xs ${esp.online ? 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30' : 'bg-muted text-muted-foreground'}`}>
              {esp.online ? 'Çevrimiçi' : 'Çevrimdışı'}
            </Badge>
          </>
        ) : (
          <>
            <div className="w-2.5 h-2.5 rounded-full bg-muted-foreground/40 shrink-0" />
            <span className="font-semibold text-base text-muted-foreground" style={{fontFamily:'Outfit,sans-serif'}}>Atanmamış Röleler</span>
          </>
        )}
        <span className="text-xs text-muted-foreground">{activeCount}/{relays.length} aktif</span>
        <div className="flex-1" />
        <Button size="sm" variant="ghost" disabled={batchToggling === 'on'}
          onClick={() => handleBatch(true)}
          className="h-7 text-xs gap-1.5 text-green-600 dark:text-green-400 hover:bg-green-500/10">
          <Power size={12} />{batchToggling === 'on' ? '...' : 'Hepsini Aç'}
        </Button>
        <Button size="sm" variant="ghost" disabled={batchToggling === 'off'}
          onClick={() => handleBatch(false)}
          className="h-7 text-xs gap-1.5 text-destructive hover:bg-destructive/10">
          <PowerOff size={12} />{batchToggling === 'off' ? '...' : 'Hepsini Kapat'}
        </Button>
      </div>

      {/* Relay grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {relays.map(relay => (
          <RelayCard
            key={relay.relay_number}
            relay={relay}
            espDeviceId={espDeviceId}
            deviceName={deviceMap[relay.relay_number]}
            onToggle={onToggle}
            onRename={onRename}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Group Card ───────────────────────────────────────────────────────────────

const GroupCard = ({ group, onToggle, onDelete, isAdmin }) => {
  const [toggling, setToggling] = useState(null);

  const handleToggle = async (state) => {
    setToggling(state ? 'on' : 'off');
    try {
      await controlRelayGroup(group.id, state);
      toast.success(`"${group.name}" ${state ? 'açıldı' : 'kapatıldı'}`);
      onToggle?.();
    } catch { toast.error('Grup kontrolü başarısız'); }
    finally { setToggling(null); }
  };

  return (
    <div className="border border-border rounded-xl p-3 flex items-center gap-3 bg-card hover:border-primary/30 transition-all" data-testid={`group-card-${group.id}`}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${group.color}20` }}>
        <Layers size={14} style={{ color: group.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{group.name}</p>
        <div className="flex gap-1 mt-0.5 flex-wrap">
          {group.relay_numbers.slice(0, 5).map(n => (
            <span key={n} className="text-[10px] font-mono-tech text-muted-foreground">R{n}</span>
          ))}
          {group.relay_numbers.length > 5 && <span className="text-[10px] text-muted-foreground">+{group.relay_numbers.length - 5}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button size="sm" variant="outline" disabled={toggling === 'on'} onClick={() => handleToggle(true)}
          className="h-7 px-2 text-xs gap-1 border-green-500/40 text-green-600 dark:text-green-400 hover:bg-green-500/10" data-testid={`group-on-${group.id}`}>
          <Power size={11} />{toggling === 'on' ? '...' : 'Aç'}
        </Button>
        <Button size="sm" variant="outline" disabled={toggling === 'off'} onClick={() => handleToggle(false)}
          className="h-7 px-2 text-xs gap-1 border-destructive/40 text-destructive hover:bg-destructive/10" data-testid={`group-off-${group.id}`}>
          <PowerOff size={11} />{toggling === 'off' ? '...' : 'Kapat'}
        </Button>
        {isAdmin && (
          <ConfirmDeleteDialog
            title="Grubu Sil" description={`"${group.name}" grubunu silmek istediğinizden emin misiniz?`}
            onConfirm={() => onDelete(group.id)}
            trigger={<Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" data-testid={`group-delete-${group.id}`}><Trash2 size={12} /></Button>}
          />
        )}
      </div>
    </div>
  );
};

// ─── Add Group Dialog ─────────────────────────────────────────────────────────

const AddGroupDialog = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [relayNums, setRelayNums] = useState([]);
  const [color, setColor] = useState('#3B82F6');
  const [saving, setSaving] = useState(false);
  const COLORS = ['#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899','#06B6D4'];
  const toggle = (n) => setRelayNums(r => r.includes(n) ? r.filter(x => x !== n) : [...r, n].sort((a,b)=>a-b));

  const handleSave = async () => {
    if (!name.trim() || !relayNums.length) { toast.error('Ad ve röle seçiniz'); return; }
    setSaving(true);
    try { await onAdd({ name: name.trim(), relay_numbers: relayNums, color }); setOpen(false); setName(''); setRelayNums([]); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1.5" data-testid="add-group-btn"><Plus size={13} /> Grup Ekle</Button>
      </DialogTrigger>
      <DialogContent data-testid="add-group-dialog">
        <DialogHeader><DialogTitle>Yeni Röle Grubu</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Grup Adı</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Toplantı Odası" data-testid="group-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Renk</Label>
            <div className="flex gap-2">{COLORS.map(c => <button key={c} onClick={() => setColor(c)} className={`w-7 h-7 rounded-lg border-2 transition-all ${color===c?'border-foreground scale-110':'border-transparent'}`} style={{backgroundColor:c}} />)}</div>
          </div>
          <div className="space-y-1.5">
            <Label>Röleler <span className="text-muted-foreground font-normal">({relayNums.length} seçili)</span></Label>
            <div className="grid grid-cols-8 gap-1.5">
              {Array.from({length:16},(_,i)=>i+1).map(n => (
                <button key={n} onClick={() => toggle(n)}
                  className={`h-8 w-8 rounded-lg text-xs font-bold font-mono-tech transition-all ${relayNums.includes(n)?'text-white shadow-sm':'bg-muted text-muted-foreground hover:bg-muted/70'}`}
                  style={relayNums.includes(n)?{backgroundColor:color}:{}}
                  data-testid={`group-relay-${n}`}>{n}</button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-group-btn">{saving ? 'Ekleniyor...' : 'Ekle'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────

const RelayPanel = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [relays, setRelays]                 = useState([]);   // global (no devices)
  const [relaysByDevice, setRelaysByDevice] = useState({});   // per device map
  const [devices, setDevices]               = useState([]);
  const [groups, setGroups]                 = useState([]);
  const [espDevices, setEspDevices]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showGroups, setShowGroups] = useState(true);
  const [allToggling, setAllToggling] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const [d, g, e] = await Promise.all([getDevices(), getRelayGroups(), getEspDevices()]);
      setDevices(d.data); setGroups(g.data); setEspDevices(e.data);

      // Röleleri her ESP cihazı için bağımsız çek
      const devicesWithRelays = e.data.filter(dev => dev.relay_numbers?.length > 0);
      if (devicesWithRelays.length > 0) {
        const entries = await Promise.all(
          devicesWithRelays.map(dev => getRelays(dev.id).then(r => [dev.id, r.data]))
        );
        const map = Object.fromEntries(entries);
        setRelaysByDevice(map);
        setRelays([]); // flat list artık kullanılmıyor
      } else {
        // ESP cihazı yoksa global röleler
        const r = await getRelays();
        setRelays(r.data);
        setRelaysByDevice({});
      }
    } catch { /* silent */ }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const { paused, toggle: togglePoll, lastUpdated } = usePolling(() => load(true), 15000);

  const handleToggle = async (relayNumber, newState, espDeviceId = 'global') => {
    // Optimistic update
    if (espDeviceId !== 'global') {
      setRelaysByDevice(prev => ({
        ...prev,
        [espDeviceId]: (prev[espDeviceId] || []).map(r =>
          r.relay_number === relayNumber ? {...r, state: newState} : r
        )
      }));
    } else {
      setRelays(prev => prev.map(r => r.relay_number === relayNumber ? {...r, state: newState} : r));
    }
    try {
      await controlRelay(relayNumber, newState, espDeviceId);
    } catch {
      // Revert on error
      if (espDeviceId !== 'global') {
        setRelaysByDevice(prev => ({
          ...prev,
          [espDeviceId]: (prev[espDeviceId] || []).map(r =>
            r.relay_number === relayNumber ? {...r, state: !newState} : r
          )
        }));
      } else {
        setRelays(prev => prev.map(r => r.relay_number === relayNumber ? {...r, state: !newState} : r));
      }
      toast.error(`Röle ${relayNumber} güncellenemedi`);
    }
  };

  const handleRename = async (relayNumber, name, espDeviceId = 'global') => {
    try {
      await renameRelay(relayNumber, name, espDeviceId);
      if (espDeviceId !== 'global') {
        setRelaysByDevice(prev => ({
          ...prev,
          [espDeviceId]: (prev[espDeviceId] || []).map(r =>
            r.relay_number === relayNumber ? {...r, name} : r
          )
        }));
      } else {
        setRelays(prev => prev.map(r => r.relay_number === relayNumber ? {...r, name} : r));
      }
      toast.success('Röle adı güncellendi');
    } catch { toast.error('Adı güncellenemedi'); }
  };

  const handleAllToggle = async (state) => {
    setAllToggling(state ? 'on' : 'off');
    setRelays(prev => prev.map(r => ({...r, state})));
    try {
      await controlAllRelays(state);
      toast.success(`Tüm röleler ${state ? 'açıldı' : 'kapatıldı'}`);
    } catch {
      setRelays(prev => prev.map(r => ({...r, state: !state})));
      toast.error('Toplu kontrol başarısız');
    } finally { setAllToggling(null); }
  };

  const deviceMap = {};
  devices.forEach(d => { if (d.relay_number) deviceMap[d.relay_number] = d.name; });

  // Build sections
  const hasDevicesWithRelays = espDevices.some(d => d.relay_numbers?.length > 0);
  const sections = hasDevicesWithRelays
    ? espDevices
        .filter(d => d.relay_numbers?.length > 0)
        .map(d => ({ esp: d, relays: relaysByDevice[d.id] || [] }))
    : [{ esp: null, relays }];

  const allRelays = sections.flatMap(s => s.relays);
  const activeCount = allRelays.filter(r => r.state).length;
  const totalCount  = allRelays.length || 16;

  return (
    <div>
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{fontFamily:'Outfit,sans-serif'}}>Röle Paneli</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {loading ? 'Yükleniyor...' : `${activeCount} / ${totalCount} röle aktif`}
          </p>
        </div>

        {/* Header actions — minimal */}
        <div className="flex items-center gap-2">
          {/* All on/off */}
          <div className="flex items-center gap-1 p-1 bg-muted rounded-xl">
            <Button size="sm" variant="ghost" onClick={() => handleAllToggle(true)} disabled={!!allToggling || loading}
              className="h-7 text-xs gap-1 hover:bg-green-500/15 hover:text-green-600 dark:hover:text-green-400" data-testid="all-on-btn">
              <Power size={12} />{allToggling === 'on' ? '...' : 'Tümünü Aç'}
            </Button>
            <div className="w-px h-4 bg-border" />
            <Button size="sm" variant="ghost" onClick={() => handleAllToggle(false)} disabled={!!allToggling || loading}
              className="h-7 text-xs gap-1 hover:bg-destructive/10 hover:text-destructive" data-testid="all-off-btn">
              <PowerOff size={12} />{allToggling === 'off' ? '...' : 'Tümünü Kapat'}
            </Button>
          </div>

          {/* Live / pause */}
          <button onClick={togglePoll}
            className={`text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${paused ? 'border-border text-muted-foreground' : 'border-green-500/30 text-green-600 dark:text-green-400 bg-green-500/5'}`}
            data-testid="poll-toggle-btn" title={paused ? 'Yenileme duraklatıldı' : 'Otomatik yenileme aktif'}>
            {paused ? '⏸' : '⟳'} {paused ? 'Duraklatıldı' : 'Canlı'}
          </button>

          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => load(true)} disabled={refreshing} data-testid="refresh-relays-btn">
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* ── Groups (collapsible) ── */}
      {(groups.length > 0 || isAdmin) && (
        <div className="mb-6">
          <button
            className="flex items-center gap-2 mb-3 group"
            onClick={() => setShowGroups(v => !v)}
          >
            <Layers size={15} className="text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">
              Hızlı Gruplar
            </span>
            {groups.length > 0 && <Badge variant="outline" className="text-xs">{groups.length}</Badge>}
            {showGroups ? <ChevronUp size={13} className="text-muted-foreground" /> : <ChevronDown size={13} className="text-muted-foreground" />}
            {isAdmin && <div onClick={e => e.stopPropagation()} className="ml-auto"><AddGroupDialog onAdd={async (data) => {
              try { const r = await createRelayGroup(data); setGroups(prev => [...prev, r.data]); toast.success('Grup eklendi'); }
              catch { toast.error('Grup eklenemedi'); }
            }} /></div>}
          </button>

          {showGroups && (
            groups.length === 0 ? (
              <p className="text-xs text-muted-foreground py-3 px-1" data-testid="empty-groups">
                Henüz grup yok. "Grup Ekle" ile oluşturun.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="groups-list">
                {groups.map(g => (
                  <GroupCard key={g.id} group={g} isAdmin={isAdmin}
                    onToggle={() => load(true)}
                    onDelete={async (id) => {
                      try { await deleteRelayGroup(id); setGroups(prev => prev.filter(x => x.id !== id)); toast.success('Silindi'); }
                      catch { toast.error('Silinemedi'); }
                    }}
                  />
                ))}
              </div>
            )
          )}
          <Separator className="mt-4" />
        </div>
      )}

      {/* ── Relay Sections by ESP Device ── */}
      {loading ? (
        <div className="space-y-3">
          {Array(8).fill(0).map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      ) : (
        <div className="space-y-8" data-testid="relay-grid">
          {sections.map(({ esp, relays: sectionRelays }) => (
            <DeviceSection
              key={esp?.id || 'global'}
              esp={esp}
              espDeviceId={esp?.id || 'global'}
              relays={sectionRelays}
              deviceMap={deviceMap}
              onToggle={handleToggle}
              onRename={handleRename}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default RelayPanel;
